import assert from "assert";
import axios from "axios";
import { File as DrsFile, Project } from "graphql/_Types";
import { getEnvVar } from "ideas.env";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { isDefined } from "utils/isDefined";
import {
  CellAddress,
  DataTableColumnDefinition,
  DataTableColumnGroupDefinition,
  DataTableStoreState,
} from "./DataTableProvider.types";
import { chain, uniq } from "lodash";
import {
  FileSource,
  FileStatus,
  ProcessingStatus,
  TaskStatus,
  ToolStatus,
} from "types/constants";
import { FileType } from "types/FileTypes";
import { Except, JsonValue } from "type-fest";
import pDebounce from "p-debounce";
import { AnalysisParamValue, SerializedSideEffects } from "./SideEffects";
import { ResourceTier } from "types/ResourceTiers";
import { immer } from "zustand/middleware/immer";
import {
  HyperFormula,
  IdeasFileNotFound,
  SimpleCellAddress,
} from "@inscopix/ideas-hyperformula";
import { writeToCache } from "../cell-value-renderers/CellValueRendererFile";
import { FileFormat } from "types/FileFormats";
import { ToolSpec } from "types/json-schemas/type_guards";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "django/queryKeys";
import { DjangoClient } from "api/client/DjangoClient";
import { components, paths } from "api/schema/api-schema";
import { ModelSchema } from "api/client/types/DjangoClient.types";

/**
 * Represents the options passed to {@link getProject}
 */
type GetProjectOptions = {
  projectKey: Project["key"];
};

type GetProjectResponse = {
  id: string;
  key: string;
  name: string;
  clone_source: string;
  date_archived: string | null;
  date_created: string;
  date_data_deleted: null;
  date_deleted: null;
  default_user_access_level: number;
  default_user_access_level_display: string;
  description: string;
  icon: string | null;
  icon_color: string;
  icon_text: string | null;
  prefix: null;
  short_description: string;
  status: number;
  tenant: {
    id: number;
    key: string;
    name: string;
    tenant_type: number;
  };
  user: {
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  date_last_active: string | null;
  active_storage_size: string | null;
  archived_storage_size: string | null;
  used_credits: number | null;
  pinned: boolean;
  permissions: {
    download: boolean;
    edit: boolean;
    execute: boolean;
    grant_access: boolean;
    upload: boolean;
    view: boolean;
    role_display: string;
    role: number;
  };
  is_demo: boolean;
  is_tool_project: boolean;
  tour_type: null;
}[];

/**
 * Gets the project with a specified key within the current tenant.
 * @param options
 * @returns The project.
 */
export const getProject = async ({ projectKey }: GetProjectOptions) => {
  const url = getEnvVar("URL_LIBRARY_PROJECT")();
  const headers = await getRequestHeaders();
  // Might be a demo project; by default the backend filters out demo projects but we can unset that
  // behavior here
  const params = { key: projectKey, is_demo: "", is_tool_project: "" };
  const { data } = await axios.get<GetProjectResponse>(url, {
    headers,
    params,
  });

  const project = data[0];
  assert(isDefined(project));
  return project;
};

/**
 * Represents the options passed to {@link getTableData}
 */
type GetTableDataOptions = {
  projectId: string;
};

type BaseTableRow = {
  id: string;
  index: number;
  order: number;
  editable: boolean;
  joinable: boolean;
  cells: {
    is_custom_formula: boolean;
    formula: string | null;
  }[];
};

type BaseTable = {
  id: string;
  key: string;
  name: string;
  description: string;
  date_created: string;
  user: string;
  folder: string | null;
  hidden: boolean;

  column_groups: {
    id: string;
    name: string;
    definition: DataTableColumnGroupDefinition;
  }[];

  columns: {
    id: string;
    key: string | undefined;
    name: string;
    is_id_column: boolean;
    pinned: boolean;
    order: number;
    width: number;
    editable: boolean;
    deletable: boolean;
    identifier_position: number | null;
    default_formula: string | null;
    required: boolean;
    help_text: string | null;
    is_result: boolean;
    definition: DataTableColumnDefinition;
    group: string | null;
  }[];
};

type DataTable = BaseTable & { kind: "data"; rows: BaseTableRow[] };

type AnalysisTable = BaseTable & {
  kind: "analysis";
  cache_enabled: boolean;

  tool_version: {
    id: number;
    version: string;
    resources: ToolSpec["resources"];
    credits: number;
    status: ToolStatus;
  };

  rows: (BaseTableRow & {
    resource_tier: ResourceTier["id"] | null;
    task: {
      id: string;
      status: ModelSchema["TaskSchema"]["status"];
      date_created: string;
      credits: number | null;
      duration: string;
      user: string;
    } | null;
    tool_version: {
      id: number;
      version: string;
      resources: ToolSpec["resources"];
      credits: number;
      status: ToolStatus;
    };
    cached: boolean;
  })[];
};

type WorkflowTable = BaseTable & {
  kind: "workflow";
  cache_enabled: boolean;
  rows: (BaseTableRow & {
    run: components["schemas"]["Run"] | null;
  })[];
  analysis_tables: AnalysisTable[];
};
/**
 * Represents the response from the server when reading data for data tables
 * and analysis tables within a project.
 */
export type GetTableDataResponse = (
  | DataTable
  | AnalysisTable
  | WorkflowTable
)[];

/**
 * React Query wrapper for getProject
 * @param projectKey
 */

export const useProject = (projectKey: string) =>
  useQuery({
    queryFn: async () => {
      const project = await getProject({ projectKey });
      return project;
    },
    queryKey: queryKeys.getProject(projectKey),
  });

/**
 * Get the table data for every data table cell within a project.
 * @param options
 * @returns The table data organized by tables, columns and rows.
 */
export const getTableData = async ({ projectId }: GetTableDataOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_PROJECT")();
  const url = `${baseUrl}formulas/${projectId}/`;
  const headers = await getRequestHeaders();
  const { data } = await axios.get<GetTableDataResponse>(url, { headers });

  return data;
};

/**
 * Represents the options passed to {@link createDataTable}
 */
type CreateDataTableOptions = {
  name: string;
  key: string;
  projectId: string;
  folder?: string | null;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when creating a data table
 */
type CreateDataTableResponse = {
  id: string;
  column_groups: GetTableDataResponse[number]["column_groups"];
  columns: GetTableDataResponse[number]["columns"];
  rows: Except<GetTableDataResponse[number]["rows"][number], "cells">[];
  date_created: string;
  user: string;
  folder: string | null;
  description: string;
  hidden: boolean;
  key: string;
};

/**
 * Creates a new data table within a project.
 * @param options
 * @returns The table's ID.
 */
export const createDataTable = async ({
  name,
  key,
  projectId,
  folder,
  sideEffects,
}: CreateDataTableOptions) => {
  const url = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const body = {
    name,
    key,
    project: projectId,
    side_effects: sideEffects,
    folder,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.post<CreateDataTableResponse>(url, body, {
    headers,
  });
  return data;
};

/**
 * Represents the options passed to {@link createAnalysisTable}
 */
type CreateAnalysisTableOptions = {
  toolVersionId: number;
  name: string;
  key: string;
  projectId: string;
  sourceTables: {
    kind: "data_table" | "gdt_analysis_table" | "workflow_table";
    id: string;
  }[];
  paramMap: {
    param: string;
    column: {
      id: string;
      kind:
        | "data_table_column"
        | "gdt_analysis_table_column"
        | "workflow_table_column";
    };
  }[];
  folder?: string | null;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when creating an analysis table
 */
type CreateAnalysisTableResponse = Pick<
  paths["/library/gdt_analysis_table/"]["post"]["responses"]["201"]["content"]["application/json"],
  "id" | "key" | "date_created" | "description" | "hidden" | "cache_enabled"
> & {
  user: string;
  folder: string | null;
  tool_version: {
    id: number;
    version: string;
    resources: ToolSpec["resources"];
    credits: number;
    status: ToolStatus;
  } | null;
  column_groups: GetTableDataResponse[number]["column_groups"];
  columns: GetTableDataResponse[number]["columns"];
  rows: Except<GetTableDataResponse[number]["rows"][number], "cells">[];
};

/**
 * Creates a new analysis table within a project.
 * @param options
 * @returns The table's ID.
 */
export const createAnalysisTable = async ({
  toolVersionId,
  name,
  key,
  projectId,
  sourceTables,
  paramMap,
  sideEffects,
  folder,
}: CreateAnalysisTableOptions) => {
  const url = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
  const body = {
    tool_version: toolVersionId,
    name,
    key,
    project: projectId,
    source_tables: sourceTables,
    param_map: paramMap,
    side_effects: sideEffects,
    folder,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.post<CreateAnalysisTableResponse>(url, body, {
    headers,
  });
  return data;
};

/**
 * Represents the options passed to {@link updateDataTable}
 */
type UpdateDataTableOptions = {
  tableId: string;
  name?: string;
  description?: string;
  key?: string;
  folder?: string | null;
  hidden?: boolean;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating a data table
 */
type UpdateDataTableResponse = {
  id: string;
};

/**
 * Updated a data table.
 * @param options
 * @returns The table's ID.
 */
export const updateDataTable = async ({
  tableId,
  name,
  description,
  key,
  folder,
  hidden,
  sideEffects,
}: UpdateDataTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const url = `${baseUrl}${tableId}/`;
  const body = {
    ...(isDefined(name) ? { name } : {}),
    ...(isDefined(description) ? { description } : {}),
    ...(isDefined(key) ? { key } : {}),
    ...(isDefined(folder) ? { folder } : {}),
    ...(isDefined(hidden) ? { hidden } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateDataTableResponse>(url, body, {
    headers,
  });
  return data.id;
};

/**
 * Represents the options passed to {@link updateAnalysisTable}
 */
type UpdateAnalysisTableOptions = {
  tableId: string;
  name?: string;
  description?: string;
  key?: string;
  folder?: string | null;
  hidden?: boolean;
  cacheEnabled?: boolean;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating an analysis table
 */
type UpdateAnalysisTableResponse = {
  id: string;
};

/**
 * Updated an analysis table.
 * @param options
 * @returns The table's ID.
 */
export const updateAnalysisTable = async ({
  tableId,
  name,
  description,
  key,
  folder,
  hidden,
  cacheEnabled,
  sideEffects,
}: UpdateAnalysisTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
  const url = `${baseUrl}${tableId}/`;
  const body = {
    ...(isDefined(name) ? { name } : {}),
    ...(isDefined(description) ? { description } : {}),
    ...(isDefined(key) ? { key } : {}),
    ...(isDefined(folder) ? { folder } : {}),
    ...(isDefined(hidden) ? { hidden } : {}),
    ...(isDefined(cacheEnabled) ? { cache_enabled: cacheEnabled } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateAnalysisTableResponse>(url, body, {
    headers,
  });
  return data.id;
};

/**
 * Represents the options passed to {@link updateAnalysisTable}
 */
type UpdateWorkflowTableOptions = {
  tableId: string;
  name?: string;
  description?: string;
  key?: string;
  folder?: string | null;
  hidden?: boolean;
  cacheEnabled?: boolean;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating an analysis table
 */
type UpdateWorkflowTableResponse = {
  id: string;
};

/**
 * Updated an Workflow table.
 * @param options
 * @returns The table's ID.
 */
export const updateWorkflowTable = async ({
  tableId,
  name,
  description,
  key,
  folder,
  hidden,
  cacheEnabled,
  sideEffects,
}: UpdateWorkflowTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_WORKFLOW_TABLE");
  const url = `${baseUrl}${tableId}/`;
  const body = {
    ...(isDefined(name) ? { name } : {}),
    ...(isDefined(description) ? { description } : {}),
    ...(isDefined(key) ? { key } : {}),
    ...(isDefined(folder) ? { folder } : {}),
    ...(isDefined(hidden) ? { hidden } : {}),
    ...(isDefined(cacheEnabled) ? { cache_enabled: cacheEnabled } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateWorkflowTableResponse>(url, body, {
    headers,
  });
  return data.id;
};

/**
 * Represents the options passed to {@link updateColumnGroup}
 */
type UpdateColumnGroupOptions = {
  tableKind: "data" | "analysis" | "workflow";
  groupId: string;
  name?: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating a column group
 */
type UpdateColumnGroupResponse = {
  id: string;
};

/**
 * Updates a column group.
 * @param options
 * @returns The column group's ID.
 */
export const updateColumnGroup = async ({
  tableKind,
  groupId,
  name,
  sideEffects,
}: UpdateColumnGroupOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data": {
        return getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN_GROUP");
      }
      case "analysis": {
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN_GROUP");
      }
      case "workflow": {
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE_COLUMN_GROUP");
      }
    }
  })();
  const url = `${baseUrl}${groupId}/`;
  const body = {
    ...(isDefined(name) ? { name } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateColumnGroupResponse>(url, body, {
    headers,
  });
  return data.id;
};

/**
 * Represents the options passed to {@link updateAnalysisTableRow}
 */
type UpdateAnalysisTableRowOptions = {
  rowId: string;
  tool_version?: number | null;
  resource_tier?: number | null;
  joinable?: boolean;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating an analysis
 * table row
 */
type UpdateAnalysisTableRowResponse = {
  id: string;
};

/*
 * Updated an analysis table row.
 * @param options
 * @returns The row's ID.
 */
export const updateAnalysisTableRow = async ({
  rowId,
  tool_version,
  resource_tier,
  joinable,
  sideEffects,
}: UpdateAnalysisTableRowOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_ROW");
  const url = `${baseUrl}${rowId}/`;
  const body = {
    ...(isDefined(tool_version) ? { tool_version } : {}),
    ...(isDefined(resource_tier) ? { resource_tier } : {}),
    ...(isDefined(joinable) ? { joinable } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateAnalysisTableRowResponse>(
    url,
    body,
    { headers },
  );
  return data.id;
};

/**
 * Represents the options passed to {@link updateColumn}
 */
type UpdateColumnOptions = {
  tableKind: "data" | "analysis" | "workflow";
  columnId: string;
  name?: string;
  width?: number;
  definition?: DataTableColumnDefinition & { project: string };
  defaultFormula?: string | null;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating a column
 */
type UpdateColumnResponse = {
  id: string;
};

/**
 * Updates a column.
 * @param options
 * @returns The column's ID.
 */
export const updateColumn = async ({
  tableKind,
  columnId,
  name,
  width,
  definition,
  defaultFormula,
  sideEffects,
}: UpdateColumnOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data": {
        return getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN");
      }
      case "analysis": {
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN");
      }
      case "workflow": {
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE_COLUMN");
      }
    }
  })();

  const url = `${baseUrl}${columnId}/`;
  const body = {
    ...(isDefined(name) ? { name } : {}),
    ...(isDefined(width) ? { width } : {}),
    ...(isDefined(definition) ? { definition } : {}),
    ...(isDefined(defaultFormula) ? { default_formula: defaultFormula } : {}),
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.patch<UpdateColumnResponse>(url, body, {
    headers,
  });
  return data.id;
};

/**
 * Represents the options passed to {@link createDataTableColumn}
 */
type CreateColumnOptions = {
  projectId: string;
  tableId: string;
  name: string;
  editable: boolean;
  defaultFormula: string | undefined;
  definition: DataTableColumnDefinition;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when creating a data table
 * column
 */
type CreateColumnResponse = {
  id: string;
  definition: DataTableColumnDefinition;
  key: string | undefined;
  name: string;
  date_created: string;
  date_deleted: null;
  is_id_column: boolean;
  pinned: boolean;
  order: number;
  width: number;
  editable: boolean;
  help_text: string | null;
  deletable: boolean;
  identifier_position: number | null;
  default_formula: string | null;
  user: string;
  table: string;
  group: null;
  required: boolean;
  is_result: boolean;
};

/**
 * Creates a new data table column.
 * @param options
 * @returns The new column.
 */
export const createDataTableColumn = async ({
  projectId,
  tableId,
  name,
  editable,
  defaultFormula,
  definition,
  sideEffects,
}: CreateColumnOptions) => {
  const url = getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN");
  const body = {
    project: projectId,
    table: tableId,
    name,
    default_formula: defaultFormula,
    definition: { ...definition, project: projectId },
    editable,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const res = await axios.post<CreateColumnResponse>(url, body, { headers });
  return res.data;
};

/**
 * Represents the options passed to {@link updateCellFormula}
 */
type UpdateCellFormulaOptions = {
  projectId: string;
  address: CellAddress;
  formula: string;
  tableKind: "data" | "analysis" | "workflow";
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when updating a cell formula
 */
type UpdateCellFormulaResponse = {
  id: string;
  formula: string;
  tenant: number;
  column: string;
  row: string;
};

/**
 * Get the formulas for every data table cell within a project.
 * @param options
 * @returns The cell formulas organized by columns and rows.
 */
export const updateCellFormula = async ({
  projectId,
  address,
  formula,
  tableKind,
  sideEffects,
}: UpdateCellFormulaOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data":
        return getEnvVar("URL_LIBRARY_DATA_TABLE_CELL");
      case "analysis":
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_CELL");
      case "workflow":
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE_CELL");
    }
  })();

  const url = `${baseUrl}set_formula/`;
  const body = {
    project: projectId,
    table: address.tableId,
    column: address.columnId,
    row: address.rowId,
    formula,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.post<UpdateCellFormulaResponse>(url, body, {
    headers,
  });
  return data.formula;
};

/**
 * Represents the options passed to {@link applySideEffects}
 */
type ApplySideEffectsOptions = {
  projectId: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Applies a list of side-effects on a project's data tables and analysis
 * tables.
 * @param options
 * @returns The serialized side-effects.
 */
export const applySideEffects = async ({
  projectId,
  sideEffects,
}: ApplySideEffectsOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_PROJECT")();
  const url = `${baseUrl}apply_side_effects/${projectId}/`;
  const body = sideEffects;
  const headers = await getRequestHeaders();
  await axios.post<SerializedSideEffects>(url, body, {
    headers,
  });
};

/**
 * Represents the options passed to {@link getProjectFiles}
 */
type GetProjectFileOptions = {
  fileReferenceId: string;
  projectId: string;
  setStoreState: Parameters<
    Parameters<typeof immer<DataTableStoreState, [], []>>[0]
  >[0];
  engine: HyperFormula;
  address: SimpleCellAddress;
};

export type GetProjectFileResponseByReferenceId = {
  id: string;
  reference_id: string;
  name: string;
  processing_status: ProcessingStatus;
  file_format: FileFormat["id"];
  file_type: FileType["id"];
  tags: string[] | null;
  status: FileStatus;
  is_series: boolean;
  series_parent_id: string | null;
  size: number;
}[];
/**
 * Represents the response returned by the server when fetching files
 */
export type GetProjectFileResponse = {
  results: GetProjectFileResponseByReferenceId;
};

/**
 * Gets all files stored in the project.
 * @param options
 * @returns The project files.
 */
export const getProjectFiles = (() => {
  // Accumulator that stores all arguments passed during the debounce interval
  const allArgs: GetProjectFileOptions[] = [];

  // Debounced function that fetches all files requested in an interval
  const callback = pDebounce(async () => {
    // Copy the accumulator and reset it. Since this happens in one step, there
    // is no risk of concurrency issues when pushing to the array.
    const args = allArgs.splice(0);

    const url = getEnvVar("URL_DRS_FILE_BY_REFERENCE_ID");
    const projectId = args[0].projectId;
    const setStoreState = args[0].setStoreState;
    const engine = args[0].engine;
    const referenceIds = uniq(args.map((arg) => arg.fileReferenceId));
    const body = {
      reference_ids: referenceIds,
      project: projectId,
    };
    const headers = await getRequestHeaders();
    const res = await axios.post<GetProjectFileResponseByReferenceId>(
      url,
      body,
      { headers },
    );

    setStoreState((state) => {
      const fetchedReferenceIds = new Set<string>();

      res.data.forEach((file) => {
        switch (file.status) {
          // Filter out deleted files
          case FileStatus["SCHEDULE_DELETE"]:
          case FileStatus["SCHEDULE_DATA_DELETE"]:
          case FileStatus["DATA_DELETED"]:
          case FileStatus["UPLOAD_CANCELLED"]:
            return;
          case FileStatus["CREATED"]:
          case FileStatus["UPLOADING"]:
          case FileStatus["AVAILABLE"]:
          case FileStatus["ARCHIVED"]:
          case FileStatus["UNARCHIVED"]:
            break;
        }

        fetchedReferenceIds.add(file.reference_id);
        state.fileMap.filesById[file.id] = file;
        state.fileMap.filesByReferenceId[file.reference_id] = file;

        writeToCache(file.id, {
          __typename: "File",
          id: file.id,
          name: file.name,
          originalName: file.name,
          status: file.status,
          fileFormat: file.file_format,
          fileType: file.file_type,
          tags: file.tags ?? [],
          isSeries: file.is_series,
          // FIXME: Correct
          source: FileSource.UPLOADED,
          processingStatus: file.processing_status,
          seriesParentId: file.series_parent_id,
          projectId,
          referenceId: file.reference_id,
        });
      });

      referenceIds.forEach((referenceId) => {
        if (!fetchedReferenceIds.has(referenceId)) {
          state.fileMap.filesByReferenceId[referenceId] = IdeasFileNotFound;
        }
      });
    });

    // Recalculate the cell formulas at each specified address
    const addresses = new Set(args.map((arg) => arg.address));
    engine.batch(() => {
      Array.from(addresses).forEach((address) => {
        const formula = engine.getCellFormula(address);
        engine.setCellContents(address, formula);
      });
    });
  }, 10);

  return async (options: GetProjectFileOptions) => {
    // Add arguments to the accumulator
    allArgs.push(options);

    // Call the debounced callback. This will resolve after the debounce
    // interval has elapsed.
    await callback();
  };
})();

/**
 * Represents the options passed to {@link getAnalysisTableRowByIds}
 */
type GetAnalysisTableCellByOptions = {
  rowIds: string[];
};

/**
 * Represents the response returned by the server when fetching files
 */
export type GetAnalysisTableCellByResponse = {
  results: {
    id: string;
    formulas: string[];
    index: number;
    table: string;
    task: {
      id: string;
      status: TaskStatus;
      date_created: string;
      credits: number | null;
      duration: string;
      user: string;
    } | null;
    cached: boolean;
    user: string;
  }[];
};

/**
 * Gets all matching analysis table cells.
 * @param options
 * @returns The analysis table cells.
 */
export const getAnalysisTableRowByIds = async ({
  rowIds,
}: GetAnalysisTableCellByOptions) => {
  const url = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_ROW");
  const headers = await getRequestHeaders();
  const params = {
    id__in: chain(rowIds).uniq().join(",").value(),
  };
  const { data } = await axios.get<GetAnalysisTableCellByResponse>(url, {
    headers,
    params,
  });
  return data;
};

/**
 * Gets all matching workflow table rows.
 * @param options
 * @returns The workflow table rows.
 */
export const getWorkflowTableRowByIds = async ({
  rowIds,
}: GetAnalysisTableCellByOptions) => {
  const res = await DjangoClient.library.workflow_table_row.list.queryFn({
    query: { id__in: [uniq(rowIds).join(",")] },
  });

  return res;
};

/**
 * Represents the options passed to {@link getFileMetadatumValue}
 */
type GetFileMetadatumValueOptions = {
  fileId: string;
  metadatumKey: string;
  setStoreState: Parameters<
    Parameters<typeof immer<DataTableStoreState, [], []>>[0]
  >[0];
  engine: HyperFormula;
  address: SimpleCellAddress;
};

/**
 * Represents the response returned by the server when fetching file metadatum
 * values
 */
type GetFileMetadatumValueResponse = {
  date_created: string;
  file: string;
  id: string;
  key: string;
  tenant: number;
  values: [
    {
      date_created: string;
      value: JsonValue;
    },
  ];
}[];

/**
 * Gets the current value of a specified file metadatum.
 * @param options
 * @returns The metadatum value.
 */
export const getFileMetadatumValue = (() => {
  // Accumulator that stores all arguments passed during the debounce interval
  const allArgs: GetFileMetadatumValueOptions[] = [];

  // Debounced function that fetches all file metadata requested in an interval
  const callback = pDebounce(async () => {
    // Copy the accumulator and reset it. Since this happens in one step, there
    // is no risk of concurrency issues when pushing to the array.
    const args = allArgs.splice(0);

    const baseUrl = getEnvVar("URL_METADATA_FILE_METADATA");
    const url = `${baseUrl}by_file_id/`;
    const setStoreState = args[0].setStoreState;
    const engine = args[0].engine;
    const fileIds = uniq(args.map((arg) => arg.fileId));
    const metadataKeys = uniq(args.map((arg) => arg.metadatumKey));
    const body = {
      file_ids: fileIds,
      metadata_keys: metadataKeys,
    };
    const headers = await getRequestHeaders();
    const res = await axios.post<GetFileMetadatumValueResponse>(url, body, {
      headers,
    });

    // Update the store state with the latest values for each file metadatum
    setStoreState((state) => {
      res.data.forEach((result) => {
        const fileId = result.file;
        const metadatumKey = result.key;
        const latestValue: JsonValue = chain(result.values)
          .sortBy((value) => value.date_created)
          .last()
          .value().value;

        const metadatumMap =
          state.metadatumMap as DataTableStoreState["metadatumMap"];
        metadatumMap[fileId] = {
          ...metadatumMap[fileId],
          ...{ [metadatumKey]: latestValue },
        };
      });

      // Set metadata value to null for any file/metadata pair that is missing
      // in response
      fileIds.forEach((fileId) => {
        metadataKeys.forEach((metadatumKey) => {
          const metadatumMap =
            state.metadatumMap as DataTableStoreState["metadatumMap"];
          if (
            !isDefined(metadatumMap[fileId]) ||
            !isDefined(metadatumMap[fileId]?.[metadatumKey])
          ) {
            metadatumMap[fileId] = {
              ...metadatumMap[fileId],
              [metadatumKey]: null,
            };
          }
        });
      });
    });

    // Recalculate the cell formulas at each specified address
    const addresses = new Set(args.map((arg) => arg.address));
    engine.batch(() => {
      Array.from(addresses).forEach((address) => {
        const formula = engine.getCellFormula(address);
        engine.setCellContents(address, formula);
      });
    });
  }, 10);

  return async (options: GetFileMetadatumValueOptions) => {
    // Add arguments to the accumulator
    allArgs.push(options);

    // Call the debounced callback. This will resolve after the debounce
    // interval has elapsed.
    await callback();
  };
})();

/**
 * Represents the options passed to {@link deleteRows}
 */
type DeleteTableOptions = {
  tableKind: "data" | "analysis" | "workflow";
  tableId: string;
  fileIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when deleting tables
 */
type DeleteTableResponse = {
  id: string;
};

/**
 * Deletes a specified table
 * @param options
 * @returns The ID of the deleted table.
 */
export const deleteTable = async ({
  tableKind,
  tableId,
  fileIds,
  sideEffects,
}: DeleteTableOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data": {
        return getEnvVar("URL_LIBRARY_DATA_TABLE");
      }
      case "analysis": {
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
      }
      case "workflow": {
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE");
      }
    }
  })();

  const url = `${baseUrl}delete/${tableId}/`;
  const headers = await getRequestHeaders();
  const body = {
    ...(tableKind === "data" ? { files: fileIds } : {}),
    side_effects: sideEffects,
  };
  const res = await axios.post<DeleteTableResponse>(url, body, { headers });
  return res.data.id;
};

/**
 * Represents the options passed to {@link deleteDataTableColumnGroup}
 */
type DeleteColumnGroupOptions = {
  groupId: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when deleting data table column groups
 */
type DeleteColumnGroupResponse = {
  id: string;
};

/**
 * Deletes a data table column group.
 * @param options
 * @returns The ID of the deleted column group.
 */
export const deleteDataTableColumnGroup = async ({
  groupId,
  sideEffects,
}: DeleteColumnGroupOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN_GROUP");
  const url = `${baseUrl}delete/${groupId}/`;
  const body = { side_effects: sideEffects };
  const headers = await getRequestHeaders();
  const res = await axios.post<DeleteColumnGroupResponse>(url, body, {
    headers,
  });
  return res.data.id;
};

/**
 * Represents the options passed to {@link deleteDataTableColumn}
 */
type DeleteColumnOptions = {
  columnId: string;
  fileIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when deleting data table columns
 */
type DeleteColumnResponse = {
  id: string;
};

/**
 * Deletes a data table column.
 * @param options
 * @returns The ID of the deleted column.
 */
export const deleteDataTableColumn = async ({
  columnId,
  fileIds,
  sideEffects,
}: DeleteColumnOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN");
  const url = `${baseUrl}delete/${columnId}/`;
  const body = { files: fileIds, side_effects: sideEffects };
  const headers = await getRequestHeaders();
  const res = await axios.post<DeleteColumnResponse>(url, body, { headers });
  return res.data.id;
};

/**
 * Deletes multiple rows.
 * @param options
 * @returns The IDs of the deleted rows.
 */
export const deleteRows = async (
  tableKind: "data" | "analysis" | "workflow",
  params: Parameters<
    typeof DjangoClient.library.data_table_row.delete_rows.queryFn
  >[0],
) => {
  switch (tableKind) {
    case "data": {
      return await DjangoClient.library.data_table_row.delete_rows.queryFn({
        ...params,
      });
    }
    case "analysis": {
      return await DjangoClient.library.gdt_analysis_table_row.delete_rows.queryFn(
        {
          ...params,
        },
      );
    }
    case "workflow": {
      return await DjangoClient.library.workflow_table_row.delete_rows.queryFn({
        ...params,
      });
    }
  }
};

/**
 * Represents the options passed to {@link deleteFile}
 */
type DeleteFileOptions = {
  fileId: string;
  dateDeleted: string;
  mode: "data_delete" | "delete";
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when deleting files
 */
export type DeleteFileResponse = {
  id: string;
  status: FileStatus;
  date_deleted: string;
};

/**
 * Deletes a file.
 * @param options
 * @returns The ID of the deleted file.
 */
export const deleteFile = async ({
  fileId,
  dateDeleted,
  mode,
  sideEffects,
}: DeleteFileOptions) => {
  const baseUrl =
    mode === "data_delete"
      ? getEnvVar("URL_DRS_FILE_SCHEDULE_DATA_DELETE")
      : getEnvVar("URL_DRS_FILE_SCHEDULE_DELETE");
  const url = `${baseUrl}${fileId}/`;
  const body = {
    date_data_deleted: mode === "data_delete" ? dateDeleted : undefined,
    date_deleted: mode === "delete" ? dateDeleted : undefined,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const res = await axios.patch<DeleteFileResponse>(url, body, { headers });
  return res.data;
};

/**
 * Represents the options passed to {@link createSeries}
 */
type CreateSeriesOptions = {
  seriesFileId: string;
  seriesFileName: string;
  seriesMemberFileIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when creating a file series
 */
export type CreateSeriesResponse = {
  id: DrsFile["id"];
  referenceId: DrsFile["referenceId"];
  key: DrsFile["key"];
  name: DrsFile["name"];
  size: DrsFile["size"];
  series_files: DrsFile["id"][];
};

/**
 * Creates a file series.
 * @param options
 * @returns The ID of the new series file.
 */
export const createSeries = async ({
  seriesFileId,
  seriesFileName,
  seriesMemberFileIds,
  sideEffects,
}: CreateSeriesOptions) => {
  const url = getEnvVar("URL_DRS_FILE_SERIES_CREATE");
  const body = {
    id: seriesFileId,
    name: seriesFileName,
    series_files: seriesMemberFileIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  const res = await axios.post<CreateSeriesResponse>(url, body, { headers });
  return res.data;
};

/**
 * Represents the options passed to {@link breakSeries}
 */
type BreakSeriesOptions = {
  fileId: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when breaking a file series
 */
export type BreakSeriesResponse = {
  restored_files: DrsFile["id"][];
};

/**
 * Breaks a file series into separate files.
 * @param options
 * @returns The IDs of the series member files.
 */
export const breakSeries = async ({
  fileId,
  sideEffects,
}: BreakSeriesOptions) => {
  const baseUrl = getEnvVar("URL_DRS_FILE_SERIES_REVERT_CREATE");
  const url = `${baseUrl}${fileId}/`;
  const body = { side_effects: sideEffects };
  const headers = await getRequestHeaders();
  const res = await axios.delete<BreakSeriesResponse>(url, {
    headers,
    data: body,
  });
  return res.data;
};

/**
 * Represents the options passed to {@link reorderColumns}
 */
type ReorderColumnsOptions = {
  tableId: string;
  tableKind: "data" | "analysis" | "workflow";
  columnIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when reordering columns
 */
type ReorderColumnsResponse = {
  id: string;
};

/**
 * Reorders column withing a data table or analysis table.
 * @param options
 * @returns The IDs of the columns in their new order.
 */
export const reorderColumns = async ({
  tableId,
  tableKind,
  columnIds,
  sideEffects,
}: ReorderColumnsOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data": {
        return getEnvVar("URL_LIBRARY_DATA_TABLE");
      }
      case "analysis": {
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
      }
      case "workflow": {
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE");
      }
    }
  })();

  const url = `${baseUrl}reorder_columns/${tableId}/`;
  const body = {
    columns: columnIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.patch<ReorderColumnsResponse>(url, body, { headers });
  return columnIds;
};

/**
 * Represents the options passed to {@link reorderIdentifiers}
 */
type ReorderIdentifiersOptions = {
  tableId: string;
  tableKind: "data" | "analysis" | "workflow";
  columnIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when reordering column
 * identifier positions
 */
type ReorderIdentifiersResponse = {
  id: string;
};

/**
 * Reorders column identifier positions within a data table or analysis table.
 * @param options
 * @returns The ID of the table.
 */
export const reorderIdentifiers = async ({
  tableId,
  tableKind,
  columnIds,
  sideEffects,
}: ReorderIdentifiersOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data": {
        return getEnvVar("URL_LIBRARY_DATA_TABLE");
      }
      case "analysis": {
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
      }
      case "workflow": {
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE");
      }
    }
  })();
  const url = `${baseUrl}reorder_identifiers/${tableId}/`;
  const body = {
    columns: columnIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.patch<ReorderIdentifiersResponse>(url, body, { headers });
  return columnIds;
};

/**
 * Represents the options passed to {@link pinColumn}
 */
type PinColumnOptions = {
  tableKind: "data" | "analysis" | "workflow";
  columnId: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when pinning columns
 */
type PinColumnResponse = {
  id: string;
};

/**
 * Pins a column withing a data table or analysis table.
 * @param options
 * @returns The IDs of the pinned columns.
 */
export const pinColumn = async ({
  tableKind,
  columnId,
  sideEffects,
}: PinColumnOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data":
        return getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN");
      case "analysis":
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN");
      case "workflow":
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE_COLUMN");
    }
  })();

  const url = `${baseUrl}pin/${columnId}/`;
  const body = { side_effects: sideEffects };
  const headers = await getRequestHeaders();
  await axios.patch<PinColumnResponse>(url, body, { headers });
  return columnId;
};

/**
 * Represents the options passed to {@link unpinColumn}
 */
type UnpinColumnOptions = {
  tableKind: "data" | "analysis" | "workflow";
  columnId: string;
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when unpinning columns
 */
type UnpinColumnResponse = {
  id: string;
};

/**
 * Unpins a column withing a data table or analysis table.
 * @param options
 * @returns The IDs of the unpinned columns.
 */
export const unpinColumn = async ({
  tableKind,
  columnId,
  sideEffects,
}: UnpinColumnOptions) => {
  const baseUrl = (() => {
    switch (tableKind) {
      case "data":
        return getEnvVar("URL_LIBRARY_DATA_TABLE_COLUMN");
      case "analysis":
        return getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_COLUMN");
      case "workflow":
        return getEnvVar("URL_LIBRARY_WORKFLOW_TABLE_COLUMN");
    }
  })();
  const url = `${baseUrl}unpin/${columnId}/`;
  const body = { side_effects: sideEffects };
  const headers = await getRequestHeaders();
  await axios.patch<UnpinColumnResponse>(url, body, { headers });
  return columnId;
};

type CreateFileInput = Pick<
  DrsFile,
  | "projectId"
  | "name"
  | "tenantId"
  | "processingStatus"
  | "fileType"
  | "fileFormat"
> & {
  size: number;
};

type CreateFileBody = {
  name: DrsFile["name"];
  part_size: DrsFile["partSize"];
  size: DrsFile["size"];
  date_created: DrsFile["dateCreated"];
  project: DrsFile["projectId"];
  uploaded: true;
  tenant: number;
  processing_status: ProcessingStatus;
  file_type: DrsFile["fileType"];
  file_format: DrsFile["fileFormat"];
};

type CreateFileResponse = {
  date_created: DrsFile["dateCreated"];
  id: DrsFile["id"];
  name: DrsFile["name"];
  project: DrsFile["projectId"];
  part_size: NonNullable<DrsFile["partSize"]>;
  size: DrsFile["size"];
  status: FileStatus;
  user: DrsFile["userId"];
  tenant: DrsFile["tenantId"];
  processing_status: ProcessingStatus;
  file_type: FileType["id"];
  file_format: DrsFile["fileFormat"];
  is_series: boolean;
  series_parent_id: string | null;
  reference_id: string;
};

/**
 * Calculates a multipart upload part size for a minimum part size of 100 MB
 * (AWS guideline) and maximum 10,000 parts (AWS limit). Part sizes will only
 * increase from the minimum for extremely large (1TB+) files.
 * @param fileSize file size in bytes
 * @returns A multipart upload part size in bytes
 */
const getPartSize = (fileSize: number) => {
  const minPartSize = 52_428_800; // 50 MB
  const maxNumParts = 10_000;
  const factor = Math.ceil(fileSize / (minPartSize * maxNumParts));
  return factor * minPartSize;
};

/**
 * Formats a filename to be compatible with the backend
 * @param filename
 * @returns The formatted filename
 */
const formatFilename = (filename: string) => {
  /*
      A valid filename may contain the following characters:
      - Letters (A-Za-z)
      - Numbers (\d)
      - Periods (.)
      - Hyphens (-)
      - Underscores (_)
    */
  const invalidCharRegExp = /[^A-Za-z\d.-_]/g;
  const replaceValue = "-";
  return filename.replace(invalidCharRegExp, replaceValue);
};

/**
 * Creates a file within a project.
 * @param options
 * @returns The new file.
 */
export const createFile = async (input: CreateFileInput) => {
  const url = getEnvVar("URL_DRS_FILE_CREATE");
  const body: CreateFileBody = {
    name: formatFilename(input.name),
    size: input.size.toString(),
    part_size: getPartSize(input.size),
    date_created: new Date().toISOString(),
    project: input.projectId,
    uploaded: true,
    tenant: input.tenantId,
    processing_status: input.processingStatus,
    file_format: input.fileFormat,
    file_type: input.fileType,
  };
  const headers = await getRequestHeaders();
  const { data } = await axios.post<CreateFileResponse>(url, body, { headers });
  return data;
};

/**
 * Represents the options passed to {@link executeAnalysisTableRows}
 */
type ExecuteAnalysisTableRowOptions = {
  rowId: string;
  paramValues: {
    column: string;
    value: AnalysisParamValue;
  }[];
  sideEffects: SerializedSideEffects;
}[];

/**
 * Represents the response returned by the server when executing analysis table
 * rows
 */
type ExecuteAnalysisTableRowResponse = {
  id: string;
  task: {
    id: string;
    status: TaskStatus;
    date_created: string;
    credits: number | null;
    duration: string;
    user: string;
  };
  cached: boolean;
}[];

/**
 * Deletes multiple rows.
 * @param options
 * @returns The IDs of the deleted rows.
 */
export const executeAnalysisTableRows = async (
  inputs: ExecuteAnalysisTableRowOptions,
) => {
  const baseUrl = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE_ROW");
  const url = `${baseUrl}bulk_execute/`;
  const body = inputs.map((input) => ({
    id: input.rowId,
    param_values: input.paramValues,
    side_effects: input.sideEffects,
  }));
  const headers = await getRequestHeaders();
  const res = await axios.post<ExecuteAnalysisTableRowResponse>(url, body, {
    headers,
  });
  assert(res.data !== undefined);
  return res.data;
};

/**
 * Represents the options passed to {@link cancelTask}
 */
type CancelTaskOptions = {
  taskId: string;
};

/**
 * Represents the response returned by the server when canceling tasks
 */
type CancelTaskResponse = {
  status: string;
};

/**
 * Cancels a running task.
 * @param options
 * @returns The ID of the canceled task.
 */
export const cancelTask = async ({ taskId }: CancelTaskOptions) => {
  const baseUrl = getEnvVar("URL_TES_TASK_CANCEL");
  const url = `${baseUrl}${taskId}/`;
  const body = undefined;
  const headers = await getRequestHeaders();
  await axios.patch<CancelTaskResponse>(url, body, { headers });
};

/**
 * Represents the options passed to {@link joinDataTable}
 */
type JoinDataTableOptions = {
  tableId: string;
  headerName: string;
  sourceTableId: string;
  leftColumnId: string;
  rightColumnId: string;
  sourceColumnIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when joining data tables
 */
type JoinDataTableResponse = {
  status: string;
};

/**
 * Joins columns from a data table into another specified data table.
 * @param options
 */
export const joinDataTable = async ({
  tableId,
  headerName,
  sourceTableId,
  leftColumnId,
  rightColumnId,
  sourceColumnIds,
  sideEffects,
}: JoinDataTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const url = `${baseUrl}join_data_table/${tableId}/`;
  const body = {
    column_group_name: headerName,
    source_table: sourceTableId,
    left_column: leftColumnId,
    right_column: rightColumnId,
    source_columns: sourceColumnIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.post<JoinDataTableResponse>(url, body, { headers });
};

/**
 * Represents the options passed to {@link linkDataTable}
 */
type LinkDataTableOptions = {
  tableId: string;
  headerName: string;
  sourceTableId: string;
  sourceColumnIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when linking data tables
 */
type LinkDataTableResponse = {
  status: string;
};

/**
 * Joins columns from a data table into another specified data table with an
 * editable identifier column.
 * @param options
 */
export const linkDataTable = async ({
  tableId,
  headerName,
  sourceTableId,
  sourceColumnIds,
  sideEffects,
}: LinkDataTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const url = `${baseUrl}link_data_table/${tableId}/`;
  const body = {
    column_group_name: headerName,
    source_table: sourceTableId,
    source_columns: sourceColumnIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.post<LinkDataTableResponse>(url, body, { headers });
};

/**
 * Represents the options passed to {@link joinAnalysisTable}
 */
type JoinAnalysisTableOptions = {
  tableId: string;
  headerName: string;
  sourceTableId: string;
  leftColumnId: string;
  rightColumnId: string;
  sourceColumnIds: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when joining analysis tables
 */
type JoinAnalysisTableResponse = {
  status: string;
};

/**
 * Joins columns from an analysis table into another specified data table.
 * @param options
 */
export const joinAnalysisTable = async ({
  tableId,
  headerName,
  sourceTableId,
  leftColumnId,
  rightColumnId,
  sourceColumnIds,
  sideEffects,
}: JoinAnalysisTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const url = `${baseUrl}join_analysis_table/${tableId}/`;
  const body = {
    column_group_name: headerName,
    source_table: sourceTableId,
    left_column: leftColumnId,
    right_column: rightColumnId,
    source_columns: sourceColumnIds,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.post<JoinAnalysisTableResponse>(url, body, { headers });
};

/**
 * Represents the options passed to {@link createAnalysisTableFromDataTable}
 */
type CreateAnalysisTableFromDataTableOptions = {
  dataTableId: string;
  folderId: string | null;
  analysisTableName: string;
  analysisTableKey: string;
  toolVersionId: number;
  autoPopulateRows: boolean;
  paramMap: {
    param: string;
    column: {
      id: string;
      kind: "data_table_column" | "gdt_analysis_table_column";
    };
  }[];
  columnGroupName: string;
  paramKeys: string[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when create an analysis table
 * from a data table
 */
type CreateAnalysisTableFromDataTableResponse = {
  status: string;
};

/**
 * Creates an analysis table from a data table.
 * @param options
 */
export const createAnalysisTableFromDataTable = async ({
  dataTableId,
  folderId,
  analysisTableName,
  analysisTableKey,
  toolVersionId,
  autoPopulateRows,
  paramMap,
  columnGroupName,
  paramKeys,
  sideEffects,
}: CreateAnalysisTableFromDataTableOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_DATA_TABLE");
  const url = `${baseUrl}create_analysis_table/${dataTableId}/`;
  const body = {
    table_name: analysisTableName,
    table_key: analysisTableKey,
    tool_version: toolVersionId,
    auto_populate_rows: autoPopulateRows,
    param_map: paramMap,
    column_group_name: columnGroupName,
    param_keys: paramKeys,
    side_effects: sideEffects,
    folder: folderId,
  };
  const headers = await getRequestHeaders();
  await axios.post<CreateAnalysisTableFromDataTableResponse>(url, body, {
    headers,
  });
};

/**
 * Represents the options passed to {@link mapAnalysisParameters}
 */
type MapAnalysisParametersOptions = {
  analysisTableId: string;
  sourceTables: {
    id: string;
    kind: "data_table" | "gdt_analysis_table" | "workflow_table";
  }[];
  paramMap: {
    param: string;
    column: {
      id: string;
      kind:
        | "data_table_column"
        | "gdt_analysis_table_column"
        | "workflow_table_column";
    };
  }[];
  sideEffects: SerializedSideEffects;
};

/**
 * Represents the response returned by the server when mapping parameters in an
 * analysis table
 */
type MapAnalysisParametersResponse = {
  id: string;
};

/**
 * Maps parameters in an analysis table.
 * @param options
 */
export const mapAnalysisParameters = async ({
  analysisTableId,
  sourceTables,
  paramMap,
  sideEffects,
}: MapAnalysisParametersOptions) => {
  const baseUrl = getEnvVar("URL_LIBRARY_GDT_ANALYSIS_TABLE");
  const url = `${baseUrl}map_parameters/${analysisTableId}/`;
  const body = {
    param_map: paramMap,
    source_tables: sourceTables,
    side_effects: sideEffects,
  };
  const headers = await getRequestHeaders();
  await axios.patch<MapAnalysisParametersResponse>(url, body, { headers });
};
